/* eslint-disable no-new */
import { Box, Divider, Grid, Stack, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import Compressor from 'compressorjs';
import { FastField, Field } from '~/components/common/Formik';
import themes, { styled } from '~/themes';
import Button from '~/components/common/Button';
import { IntegrationStatus, IStore, StorePlatform } from '~/models/stores';
import { useLocation } from 'react-router-dom';
import { fromQueryString } from '~/utils/queryString';
import { useUpdateConnectionMutation } from '~/services/api/userManagement';
import { UpdateStoreSchema } from '~/utils/schema/stores';
import { useCheckConnectionInAdmin } from '~/services/api/stores';
import { queryClient } from '~/lib/react-query';
import { queryKeys } from '~/services/api/queryKeys';
import { CanFleetPluginURL } from '~/constants/stores';
import { getPlatformIcon } from '~/utils/settings';
import { useAuth } from '~/providers/AuthProvider';
import { usePaymentInfo } from '~/providers/PaymentProvider';
import ActionBar from '~/pages/AdminOptions/components/ActionBar';
import { useIsFetching } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import resetIcon from '~/assets/images/icons/reset.svg';
import externalLinkIcon from '~/assets/images/icons/external-link.svg';
import PreviewIcon from '~/assets/images/icons/preview.svg';
import {
  ColorPicker,
  isValidHex,
} from '~/components/common/ColorPicker/ColorPicker';
import { ImageUploadProvider } from '~/components/common/ImageUpload';
import UploadIcon from '~/assets/images/icons/upload-gray.svg';
import PictureIcon from '~/assets/images/icons/picture.svg';
import { useState } from 'react';
import { ToolTipText } from '~/components/common/Tooltip/TooltipText';
import { StyledPlatformIcon } from '../../../style';
import Preview from './components/Preview';

const StyledTitle = styled(Typography)(({ theme }) => ({
  color: theme.color.violet500,
  fontWeight: 700,
}));

type GeneralProps = {
  storeData: IStore;
};

const compressImageToBase64 = (
  file: File,
  quality: number = 0.7,
): Promise<string> =>
  new Promise((resolve, reject) => {
    new Compressor(file, {
      quality,
      convertSize: Infinity,
      success(result) {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(result);
      },
      error(err) {
        reject(err);
      },
    });
  });

export const General: React.FC<GeneralProps> = ({ storeData }) => {
  const { countries, isFetchingCountry } = useAuth();
  const { org, isBundlePlan } = usePaymentInfo();
  const [openPreview, setOpenPreview] = useState(false);

  const fetchingStore = useIsFetching([queryKeys.stores.getListIntegration]);
  const [debouncedFetchingStore] = useDebounce(fetchingStore, 150);

  const { mutate: updateStoreMutation, isLoading: isUpdateLoading } =
    useUpdateConnectionMutation();

  const {
    mutate: checkConnectionMutation,
    isLoading: isCheckConnectionLoading,
  } = useCheckConnectionInAdmin({
    onSuccess: (resp) => {
      if (storeData.connected !== resp.connected) {
        queryClient.invalidateQueries([queryKeys.stores.getListIntegration]);
      }
    },
  });

  const location = useLocation();
  const search = fromQueryString(location.search);
  const platform = search.platform as StorePlatform;

  const renderWooStoreInfo = (values, setFieldValue) => (
    <Grid container spacing={2} columnSpacing={[1.6, 1.6]}>
      <Grid item xs={12} md={12}>
        <StyledTitle variant='h5'>Store Location</StyledTitle>
      </Grid>
      <Grid item xs={12} md={6}>
        <Field.TextInput legend='Store Name' name='display_name' />
      </Grid>
      <Grid item xs={12} md={6}>
        <Field.TextInput legend='Phone Number' name='phone' />
      </Grid>
      <Grid item xs={12} md={6}>
        <Field.TextInput legend='Address Line 1' name='address_1' />
      </Grid>
      <Grid item xs={12} md={6}>
        <Field.TextInput legend='Address Line 2' name='address_2' />
      </Grid>
      <Grid item xs={12} md={6}>
        <Field.Select
          legend='Country'
          name='country'
          options={countries}
          sortOption
          disabled={isFetchingCountry}
          onChange={() => {
            setFieldValue('state', '');
          }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Field.Select
          legend='Province / State'
          name='state'
          options={
            countries.find(({ value }) => value === values.country)?.states ||
            []
          }
          sortOption
          disabled={isFetchingCountry}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FastField.TextInput legend='City' name='city' />
      </Grid>
      <Grid item xs={12} md={6}>
        <FastField.TextInput legend='Postal Code' name='postcode' />
      </Grid>
    </Grid>
  );

  const renderStoreLocation = () => (
    <Stack spacing={1.6} sx={{ my: 2 }}>
      <Typography fontWeight={700} color={themes.color.violet500}>
        Store Location
      </Typography>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Typography>
          {storeData?.hubs?.length} locations synced from Breadstack
        </Typography>
        {platform === StorePlatform.Breadstack && (
          <Button
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: '4px',
              alignItems: 'center',
            }}
            noRounder
            buttonType='text'
            onClick={() => window.open(storeData.breadstack_url, '_blank')}
          >
            <img src={externalLinkIcon} alt='Reset' width='16' height='16' />
            Manage in Breadstack
          </Button>
        )}
      </Stack>
    </Stack>
  );

  const renderBranding = ({ brand_color, brand_logo }, setFieldValue) => (
    <Stack>
      <StyledTitle variant='h5'>Store Branding</StyledTitle>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '8px',
          alignItems: 'center',
          mt: '4px',
        }}
      >
        <Typography>
          Branding is display on outbound emails and the delivery tracking page.
        </Typography>
        <Button
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '4px',
            alignItems: 'center',
          }}
          buttonType='text'
          noRounder
          onClick={() => setOpenPreview(true)}
          disabled={!brand_color && !brand_logo}
        >
          <img src={PreviewIcon} alt='preview' />
          Preview
        </Button>
      </Stack>
      <Stack sx={{ mt: 1.6 }}>
        <Grid container spacing={2} columnSpacing={[1.6, 1.6]}>
          <Grid item xs={12} md={6}>
            <Typography fontSize={12} fontWeight={500} mb={0.4}>
              Brand color (hex)
            </Typography>
            <ColorPicker
              onChange={(newColor) => setFieldValue('brand_color', newColor)}
              color={brand_color}
            />
          </Grid>
          <Grid item xs={12} md={6} />
          <Grid item xs={12} md={6}>
            <Typography fontSize={12} fontWeight={500} mb={0.4}>
              Brand logo
            </Typography>
            <ImageUploadProvider
              src={brand_logo || ''}
              optional
              onSave={async (_, blob: Blob) => {
                if (blob) {
                  const blobToFile = (b: Blob, fileName: string): File =>
                    new File([b], fileName, { type: b.type });
                  const base64 = await compressImageToBase64(
                    blobToFile(blob, 'brand_logo'),
                    0.7,
                  );
                  setFieldValue('brand_logo', base64 || null);
                } else setFieldValue('brand_logo', null);
              }}
              title='Brand logo'
              accept='.jpg,.jpeg,.png'
              fileSize={10}
            >
              {({ setOpen, imgSrc }) => (
                <Stack>
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt='brand logo'
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        objectPosition: 'center',
                      }}
                      crossOrigin='anonymous'
                    />
                  ) : (
                    <Box
                      sx={{
                        minHeight: '150px',
                        backgroundColor: themes.bg.gray200,
                        borderRadius: '2px',
                        border: '1px solid #E2E7F0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <img src={PictureIcon} alt='brand logo' />
                    </Box>
                  )}

                  <Typography
                    sx={{
                      color: 'rgba(51, 54, 61, 0.40)',
                      mt: 0.5,
                    }}
                    fontFamily='Lato'
                  >
                    Supported formats: jpg, png (Max 10 MB)
                  </Typography>
                  <Button
                    buttonType='text'
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '4px',
                      width: 'fit-content',
                      mt: 1.6,
                    }}
                    onClick={setOpen}
                  >
                    <img src={UploadIcon} alt='upload-icon' />
                    <Typography sx={{ fontWeight: 500 }}>
                      Upload image
                    </Typography>
                  </Button>
                </Stack>
              )}
            </ImageUploadProvider>
          </Grid>
          <Grid item xs={12} md={6} />
        </Grid>
      </Stack>
    </Stack>
  );

  const pluginUrl =
    platform === StorePlatform.Breadstack
      ? CanFleetPluginURL.Breadstack
      : CanFleetPluginURL.Woo;

  return (
    <Formik
      initialValues={{
        display_name: '',
        phone: '',
        address_1: '',
        address_2: '',
        country: '',
        city: '',
        state: '',
        postcode: '',
        id: '',
        brand_color: '',
        brand_logo: null,
        ...storeData,
        status: storeData?.status === IntegrationStatus.Active,
      }}
      enableReinitialize
      validationSchema={
        platform === StorePlatform.Breadstack ? null : UpdateStoreSchema
      }
      onSubmit={(values) => {
        let params;
        if (platform === StorePlatform.Breadstack) {
          params = {
            id: values.id,
            status: values.status
              ? IntegrationStatus.Active
              : IntegrationStatus.Deactivated,
            brand_color: values.brand_color || '',
            brand_logo: values.brand_logo ? values.brand_logo : null,
          };
        } else {
          params = {
            ...values,
            status: values.status
              ? IntegrationStatus.Active
              : IntegrationStatus.Deactivated,
          };
        }

        updateStoreMutation(params);
      }}
    >
      {({ values, dirty, setFieldValue, submitForm, resetForm, isValid }) => (
        <Form
          style={{
            color: themes.color.violet500,
          }}
        >
          <StyledTitle variant='h5'>Storefront</StyledTitle>
          <Grid
            container
            sx={{
              p: 1.6,
              mt: 1.6,
              bgcolor: themes.bg.gray700,
              color: themes.color.violet500,
              borderRadius: '4px',
              [themes.breakpoints.down('md')]: {
                gap: 1.6,
              },
            }}
          >
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '11px',
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Storefront Connection
              </Typography>
              <Field.SwitchButton
                label={values.status ? 'Enabled' : 'Disabled'}
                name='status'
                disabled={
                  Boolean(org?.bs_org_id || isBundlePlan) &&
                  platform === StorePlatform.Breadstack
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Plug-in Connection
              </Typography>
              <Typography
                sx={{
                  lineHeight: '22px',
                }}
              >
                {values.connected ? 'Connected' : 'Not Connected'}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                Store URL
              </Typography>
              <Stack
                direction='row'
                alignItems='center'
                spacing={1}
                sx={{
                  overflow: 'hidden',
                  minWidth: 0,
                  width: '100%',
                  height: '24px',
                }}
              >
                <StyledPlatformIcon
                  src={getPlatformIcon(platform)}
                  alt='platform-icon'
                  sx={{
                    flexShrink: 0,
                  }}
                />
                <ToolTipText
                  text={storeData.store_url}
                  maxLength={29}
                  textNode={(v) => (
                    <Typography
                      sx={{
                        color: themes.color.blue300,
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <a
                        href={storeData.store_url}
                        data-testid='store-url'
                        target='_blank'
                        rel='noreferrer'
                        style={{ textDecoration: 'none' }}
                      >
                        {v}
                      </a>
                    </Typography>
                  )}
                />
              </Stack>
            </Grid>
          </Grid>
          <Stack
            sx={{
              my: 1.6,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '8px',
              minHeight: '33px',
            }}
          >
            <Typography color='black'>
              Make sure you install the{' '}
              <a href={pluginUrl} rel='noreferrer'>
                Breadstack Delivery Plugin
              </a>
            </Typography>
            <Button
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '4px',
                alignItems: 'center',
              }}
              buttonType='text'
              noRounder
              loading={isCheckConnectionLoading}
              onClick={() =>
                checkConnectionMutation({ integration_id: storeData.id })
              }
            >
              <img src={resetIcon} alt='Reset' width='16' height='16' />
              Check connection
            </Button>
          </Stack>
          <Divider sx={{ my: 2 }} />

          {platform === StorePlatform.Woo
            ? renderWooStoreInfo(values, setFieldValue)
            : ''}

          {platform === StorePlatform.Breadstack ? renderStoreLocation() : ''}

          <Divider sx={{ my: 2 }} />

          {renderBranding(values, setFieldValue)}

          {!debouncedFetchingStore && dirty ? (
            <Box className='save-change-btn'>
              <ActionBar
                onCancel={resetForm}
                onSave={submitForm}
                loading={isUpdateLoading}
                disabled={
                  !isValid ||
                  (values.brand_color && !isValidHex(values.brand_color))
                }
              />
            </Box>
          ) : null}

          <Preview
            open={openPreview}
            onClose={() => setOpenPreview(false)}
            brand_color={values.brand_color}
            brand_logo={values.brand_logo}
          />
        </Form>
      )}
    </Formik>
  );
};
